import { Injectable, inject, signal } from '@angular/core';
import { AnalysisDetailStore } from '@features/analyses/store/analysis-detail.store';
import { AnalysisService } from '@shared/services/analysis.service';
import { AddressService } from '@shared/services/address.service';
import { ToastService } from '@shared/services/toast.service';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import { BusinessStatus } from '@shared/models/analysis.model';
import { environment } from '@env/environment';

const debugLog = (...args: unknown[]) => {
  if (!environment.production) {
    console.log(...args);
  }
};

/**
 * Facade that encapsulates all analysis data-update operations:
 * business status changes, detail field edits, and map coordinate saves.
 *
 * Responsibilities:
 * - Owns isSavingMarkerPosition UI state signal
 * - Coordinates multi-resource updates (analysis + address in parallel)
 * - Triggers store reloads after successful mutations
 *
 * Provide at component level so its signal is scoped to the detail page lifetime.
 */
@Injectable()
export class AnalysisUpdateFacade {
  private readonly store = inject(AnalysisDetailStore);
  private readonly analysisService = inject(AnalysisService);
  private readonly addressService = inject(AddressService);
  private readonly toastService = inject(ToastService);
  private readonly currentOrgStore = inject(CurrentOrganizationStore);

  readonly isSavingMarkerPosition = signal(false);
  readonly isSavingHasSolar = signal(false);

  readonly detailsSaveStatus = signal<'idle' | 'saving' | 'saved' | 'error'>('idle');
  readonly detailsLastSavedAt = signal<Date | null>(null);

  private detailsStatusResetTimer: ReturnType<typeof setTimeout> | null = null;

  private get analysis() {
    return this.store.selectDetail();
  }

  private get organizationId(): string | null {
    return (
      this.currentOrgStore.organization()?.id ||
      this.analysis?.organizationId ||
      null
    );
  }

  // ─── Public API ───────────────────────────────────────────────────────────

  async onStatusChanged(newStatus: BusinessStatus): Promise<void> {
    const orgId = this.organizationId;
    const analysis = this.analysis;
    if (!orgId || !analysis) return;

    try {
      await this.analysisService.updateAnalysisStatus(
        orgId,
        analysis.analysisId,
        newStatus,
      );
      this.toastService.success('Status aktualisiert');
      this.store.reloadCurrentAnalysis(orgId);
    } catch (error) {
      debugLog('[AnalysisUpdateFacade] Failed to update status', error);
      this.toastService.error('Status konnte nicht geändert werden');
    }
  }

  async onDetailsUpdated(values: {
    powerConsumption: number;
    electricityPrice: number;
    addressData: {
      roofType?: 'flat' | 'normal' | 'both' | null;
      isApartmentBuilding?: boolean | null;
      hasSolar?: boolean | null;
    };
  }): Promise<void> {
    const orgId = this.organizationId;
    const analysis = this.analysis;
    if (!orgId || !analysis) return;
    const planningInputsChangedAt = new Date().toISOString();

    // Build optimistic patch so changes are visible immediately
    const optimisticPatch: Partial<typeof analysis> = {
      powerConsumption: values.powerConsumption,
      electricityPrice: values.electricityPrice,
      metadata: {
        ...(analysis.metadata ?? {}),
        planningInputsChangedAt,
      },
      address: analysis.address
        ? {
            ...analysis.address,
            roofType:
              values.addressData.roofType !== undefined
                ? (values.addressData.roofType as any)
                : analysis.address.roofType,
            isApartmentBuilding:
              values.addressData.isApartmentBuilding !== undefined
                ? (values.addressData.isApartmentBuilding as any)
                : analysis.address.isApartmentBuilding,
            hasSolar:
              values.addressData.hasSolar !== undefined
                ? (values.addressData.hasSolar as any)
                : analysis.address.hasSolar,
          }
        : analysis.address,
    };

    const rollbackHandle =
      this.store.updateAnalysisOptimistically(optimisticPatch);

    this.detailsSaveStatus.set('saving');
    if (this.detailsStatusResetTimer) clearTimeout(this.detailsStatusResetTimer);

    try {
      const promises: Promise<unknown>[] = [
        this.analysisService.updateAnalysis(orgId, analysis.analysisId, {
          powerConsumption: values.powerConsumption,
          electricityPrice: values.electricityPrice,
          customFields: { planningInputsChangedAt },
        } as any),
      ];

      if (analysis.addressId) {
        promises.push(
          this.addressService.updateAddress(orgId, analysis.addressId, {
            roofType: values.addressData.roofType,
            isApartmentBuilding:
              values.addressData.isApartmentBuilding !== null
                ? values.addressData.isApartmentBuilding
                : undefined,
            hasSolar:
              values.addressData.hasSolar !== null
                ? values.addressData.hasSolar
                : undefined,
          } as any),
        );
      }

      await Promise.all(promises);
      this.detailsSaveStatus.set('saved');
      this.detailsLastSavedAt.set(new Date());
      this.detailsStatusResetTimer = setTimeout(() => this.detailsSaveStatus.set('idle'), 3000);
      this.store.reloadCurrentAnalysis(orgId);
    } catch (error) {
      rollbackHandle?.rollback();
      debugLog('[AnalysisUpdateFacade] Failed to update details', error);
      this.detailsSaveStatus.set('error');
      this.toastService.error('Daten konnten nicht aktualisiert werden');
    }
  }

  async onMapCoordinatesChanged(event: {
    latitude: number;
    longitude: number;
  }): Promise<void> {
    const orgId = this.organizationId;
    const analysis = this.analysis;
    if (!orgId || !analysis?.addressId) return;

    const currentLatitude = analysis.address?.latitude;
    const currentLongitude = analysis.address?.longitude;
    const planningInputsChangedAt = new Date().toISOString();
    const roundedLatitude = Math.round(event.latitude * 100000000) / 100000000;
    const roundedLongitude =
      Math.round(event.longitude * 100000000) / 100000000;

    const hasChanged =
      currentLatitude == null ||
      currentLongitude == null ||
      Math.abs(currentLatitude - roundedLatitude) > 0.00000001 ||
      Math.abs(currentLongitude - roundedLongitude) > 0.00000001;

    if (!hasChanged || this.isSavingMarkerPosition()) return;

    const rollbackHandle = this.store.updateAnalysisOptimistically({
      metadata: {
        ...(analysis.metadata ?? {}),
        planningInputsChangedAt,
      },
      address: analysis.address
        ? {
            ...analysis.address,
            latitude: roundedLatitude,
            longitude: roundedLongitude,
          }
        : analysis.address,
    });

    try {
      this.isSavingMarkerPosition.set(true);
      await Promise.all([
        this.addressService.updateAddress(orgId, analysis.addressId, {
          latitude: roundedLatitude,
          longitude: roundedLongitude,
        }),
        this.analysisService.updateAnalysis(orgId, analysis.analysisId, {
          customFields: { planningInputsChangedAt },
        } as any),
      ]);
      this.toastService.success('Koordinaten aktualisiert');
      this.store.reloadCurrentAnalysis(orgId);
    } catch (error) {
      rollbackHandle?.rollback();
      debugLog(
        '[AnalysisUpdateFacade] Failed to update map coordinates',
        error,
      );
      this.toastService.error('Koordinaten konnten nicht aktualisiert werden');
    } finally {
      this.isSavingMarkerPosition.set(false);
    }
  }

  async onHasSolarChanged(hasSolar: boolean): Promise<void> {
    const orgId = this.organizationId;
    const analysis = this.analysis;
    if (!orgId || !analysis?.addressId || !analysis.address) return;
    if (analysis.address.hasSolar === hasSolar || this.isSavingHasSolar()) return;

    const planningInputsChangedAt = new Date().toISOString();
    try {
      this.isSavingHasSolar.set(true);
      await Promise.all([
        this.addressService.updateAddress(orgId, analysis.addressId, { hasSolar }),
        this.analysisService.updateAnalysis(orgId, analysis.analysisId, {
          customFields: { planningInputsChangedAt },
        } as any),
      ]);
      this.store.updateAnalysisOptimistically({
        metadata: { ...(analysis.metadata ?? {}), planningInputsChangedAt },
        address: { ...analysis.address, hasSolar },
      });
      this.toastService.success('Solarstatus aktualisiert');
      this.store.reloadCurrentAnalysis(orgId);
    } catch (error) {
      debugLog('[AnalysisUpdateFacade] Failed to update solar status', error);
      this.toastService.error('Solarstatus konnte nicht aktualisiert werden');
    } finally {
      this.isSavingHasSolar.set(false);
    }
  }

  async markPlanningInputsChanged(): Promise<void> {
    const orgId = this.organizationId;
    const analysis = this.analysis;
    if (!orgId || !analysis) return;

    const planningInputsChangedAt = new Date().toISOString();
    const rollbackHandle = this.store.updateAnalysisOptimistically({
      metadata: {
        ...(analysis.metadata ?? {}),
        planningInputsChangedAt,
      },
    });

    try {
      await this.analysisService.updateAnalysis(orgId, analysis.analysisId, {
        customFields: { planningInputsChangedAt },
      } as any);
    } catch (error) {
      rollbackHandle?.rollback();
      debugLog('[AnalysisUpdateFacade] Failed to mark planning as stale', error);
    }
  }
}
